import api from './client';

export type PlanKey = 'starter' | 'professional' | 'business';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete';

export type FeatureKey =
  | 'marketplace'
  | 'birdchatter'
  | 'knowledge_library'
  | 'ai_species_enrichment'
  | 'advanced_analytics'
  | 'multi_user'
  | 'api_access';

export interface PlanLimits {
  birds: number | null;
  users: number | null;
  knowledgeSources: number | null;
  aiInvocationsPerMonth: number | null;
  storageGb: number | null;
  alwaysOnCompute: boolean;
}

export interface PlanDefinition {
  key: PlanKey;
  name: string;
  description: string;
  priceMonthlyUsd: number;
  priceMonthlyZar: number;
  features: FeatureKey[];
  limits: PlanLimits;
  paymentLink: string | null;
}

export interface EntitlementsStatus {
  instanceId: string;
  billingConfigured: boolean;
  plan: PlanKey;
  planName: string;
  status: SubscriptionStatus;
  features: FeatureKey[];
  limits: PlanLimits;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Stripe will charge again at period end when true. */
  autoRenew: boolean;
  inGracePeriod: boolean;
  hasAccess: boolean;
  manageUrl: string | null;
  paymentProvider: 'paystack' | 'stripe' | 'none';
  hasPaidSubscription: boolean;
  canOpenPortal?: boolean;
  hasLinkedSubscription?: boolean;
  /** @deprecated Alias of hasPaidSubscription for older clients. */
  hasStripeSubscription: boolean;
  billingExempt?: boolean;
}

export const PLAN_RANK: Record<PlanKey, number> = {
  starter: 0,
  professional: 1,
  business: 2,
};

export const billingApi = {
  getStatus: async (): Promise<EntitlementsStatus> => {
    const res = await api.get<EntitlementsStatus>('billing/status');
    return res.data;
  },
  getPlans: async (): Promise<PlanDefinition[]> => {
    const res = await api.get<PlanDefinition[]>('billing/plans');
    return res.data;
  },
  createCheckout: async (plan: PlanKey): Promise<{ url: string }> => {
    const res = await api.post<{ url: string }>('billing/checkout', { plan });
    return res.data;
  },
  changePlan: async (
    plan: PlanKey,
  ): Promise<EntitlementsStatus | { url: string; checkout: true }> => {
    const res = await api.post<EntitlementsStatus | { url: string; checkout: true }>(
      'billing/change-plan',
      { plan },
    );
    return res.data;
  },
  cancelSubscription: async (immediate = false): Promise<EntitlementsStatus> => {
    const res = await api.post<EntitlementsStatus>('billing/cancel', { immediate });
    return res.data;
  },
  resumeSubscription: async (): Promise<EntitlementsStatus> => {
    const res = await api.post<EntitlementsStatus>('billing/resume', {});
    return res.data;
  },
  renewSubscription: async (): Promise<
    EntitlementsStatus | { url: string; checkout: true }
  > => {
    const res = await api.post<EntitlementsStatus | { url: string; checkout: true }>(
      'billing/renew',
      {},
    );
    return res.data;
  },
  createPortal: async (): Promise<{ url: string; checkout?: boolean }> => {
    const res = await api.post<{ url: string; checkout?: boolean }>('billing/portal', {});
    return res.data;
  },
  applyRebate: async (days: number, note?: string): Promise<EntitlementsStatus> => {
    const res = await api.post<EntitlementsStatus>('billing/rebate', { days, note });
    return res.data;
  },
};
